import React from 'react';
import { HiOutlineExclamationCircle, HiOutlineEye, HiOutlineEyeOff } from '../icons';

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  variant?: 'default' | 'filled' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

const Input = React.forwardRef<React.ElementRef<'input'>, InputProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      fullWidth = false,
      variant = 'default',
      size = 'md',
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      className = '',
      type = 'text',
      disabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [inputType, setInputType] = React.useState(type);

    React.useEffect(() => {
      if (showPasswordToggle && type === 'password') {
        setInputType(showPassword ? 'text' : 'password');
      } else {
        setInputType(type);
      }
    }, [showPassword, type, showPasswordToggle]);

    const baseClasses =
      'block transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      default: 'border border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500',
      filled: 'border-0 bg-gray-100 focus:bg-white focus:ring-blue-500',
      outlined: 'border-2 border-gray-300 bg-transparent focus:border-blue-500 focus:ring-blue-500',
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm rounded-md',
      md: 'px-4 py-2 text-sm rounded-lg',
      lg: 'px-5 py-3 text-base rounded-lg',
    };

    const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';

    const widthClass = fullWidth ? 'w-full' : '';
    const leftPadding = leftIcon ? 'pl-10' : '';
    const rightPadding = rightIcon || showPasswordToggle ? 'pr-10' : '';

    const inputClasses =
      `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${errorClasses} ${widthClass} ${leftPadding} ${rightPadding} ${className}`.trim();

    const containerClasses = fullWidth ? 'w-full' : '';

    return (
      <div className={containerClasses}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400">{leftIcon}</span>
            </div>
          )}

          <input
            ref={ref}
            type={inputType}
            disabled={disabled}
            className={inputClasses}
            {...props}
          />

          {showPasswordToggle && type === 'password' && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <HiOutlineEyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <HiOutlineEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          )}

          {!showPasswordToggle && rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-gray-400">{rightIcon}</span>
            </div>
          )}
        </div>

        {(error || helperText) && (
          <div className="mt-1 flex items-center">
            {error && (
              <>
                <HiOutlineExclamationCircle className="h-4 w-4 text-red-500 mr-1" />
                <span className="text-sm text-red-600">{error}</span>
              </>
            )}
            {!error && helperText && <span className="text-sm text-gray-500">{helperText}</span>}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
